/* eslint-disable react/no-unstable-nested-components */
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialReactTable, type MRT_ColumnDef, } from 'material-react-table';
import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
import { getStripedRowsForTable } from 'services/util';
import Btn, { BtnModifier} from 'components/btn';

export interface LedgerLiveSyncedJobsTableProps {
  isLoading: boolean;
  isError: boolean;
  data: LedgerLiveSyncedJobsTableDef[];
}

export interface LedgerLiveSyncedJobsTableDef {
  id: number;
  platform: string;
  address: string;
  name: string;
}

function LedgerLiveSyncedJobsTable(props: LedgerLiveSyncedJobsTableProps) {
  const tableRef = useRef(); // <<MRT_TableInstance<TData> | null>>;
  const { t } = useTranslation();

  const handleNavigation = (platform: string) => {
    let url = '';
    switch (platform) {
      case 'btc_wallet':
        url = '/import/bitcoin_address/';
        break;
      case 'avax_wallet':
        url = '/import/avax_address/';
        break;
      case 'eth_wallet_v2':
        url = '/import/eth_address/';
        break;
      case 'xrp_wallet':
        url = '/import/ripple_address/';
        break;
      case 'bsc_wallet':
        url = '/import/bsc_address/';
        break;
      case 'sol_wallet':
        url = '/import/sol_address/';
        break;
      case 'doge_wallet':
        url = '/import/dogecoin_address/';
        break;
      case 'trx_wallet':
        url = '/import/trx_address/';
        break;
      case 'ada_wallet':
        url = '/import/cardano_address/';
        break;
      case 'ton_wallet':
        url = '/import/ton_address/';
        break;
      case 'xlm_wallet':
        url = '/import/xlm_address/';
        break;
      case 'hbar_wallet':
        url = '/import/hbar_address/';
        break;
      case 'sui_wallet':
        url = '/import/sui_address/';
        break;
      case 'bch_wallet':
        url = '/import/bitcoin_cash_address/';
        break;
      case 'ltc_wallet':
        url = '/import/litecoin_address/';
        break;
      case 'dot_wallet':
        url = '/import/dot_address/';
        break;
      case 'cro_wallet':
        url = '/import/cro_address/';
        break;
      case 'aeth_wallet':
        url = '/import/aeth_address/';
        break;
      case 'algo_wallet':
        url = '/import/algo_address/';
        break;
      case 'atom_wallet':
        url = '/import/atom_address/';
        break;
      case 'base_wallet':
        url = '/import/base_address/';
        break;
      case 'blast_wallet':
        url = '/import/blast_address/';
        break;
      case 'dash_wallet':
        url = '/import/dash_address/';
        break;
      case 'egld_wallet':
        url = '/import/egld_address/';
        break;
      case 'flr_wallet':
        url = '/import/flr_address/';
        break;
      case 'ftm_wallet':
        url = '/import/ftm_address/';
        break;
      case 'glmr_wallet':
        url = '/import/glmr_address/';
        break;
      case 'inj_wallet':
        url = '/import/inj_address/';
        break;
      case 'linea_wallet':
        url = '/import/linea_address/';
        break;
      case 'matic_wallet':
        url = '/import/matic_address/';
        break;
      case 'metis_wallet':
        url = '/import/metis_address/';
        break;
      case 'near_wallet':
        url = '/import/near_address/';
        break;
      case 'op_wallet':
        url = '/import/op_address/';
        break;
      case 'osmo_wallet':
        url = '/import/osmo_address/';
        break;
      case 'sgb_wallet':
        url = '/import/sgb_address/';
        break;
      case 'vet_wallet':
        url = '/import/vet_address/';
        break;
      case 'xtz_wallet':
        url = '/import/xtz_address/';
        break;
      case 'zksync_wallet':
        url = '/import/zksync_address/';
        break;
    }

    if (url) window.open(url, '_self');
  };

  const columns = useMemo<MRT_ColumnDef<LedgerLiveSyncedJobsTableDef>[]>(() => [
    {
      accessorKey: 'name',
      size: 30,
      minSize: 30,
      enableMultiSort: true,
      header: t('Wallet', { ns: 'overview' }),
      Cell: (info) => {
        const items: string = info.cell.getValue() as string | '';
        const translatedItems: string = (items === '* no value *') ? t('* no value *', { ns: 'overview' }) : items;

        return !!items && items !== ''
          ? (
            <PlatformCard
            logo_uri={undefined}
            name={translatedItems}
            display_name={translatedItems}
            beta={false}
            modifiers={[PlatformCardModifier.OLD, PlatformCardModifier.OVERVIEW, PlatformCardModifier.SMALL_HEIGHT]}
          />
          )
          : <p />;
      },
    },
    {
      accessorKey: 'address',
      enableMultiSort: true,
      minSize: 100,
      header: t('Address', { ns: 'overview' }),
      Cell: ({ cell }) => (
        <div
          className="ct_tableBase-text-wrap-one-line"
        >
          {cell.getValue<string>() || ''}

        </div>
      ),
    },
    {
      accessorKey: 'id',
      enableSorting: false,
      minSize: 20,
      size: 20,
      header: '',
      Cell: ({ cell }) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', }}>
          <Btn
            type="link"
            text={t('Open', { ns: 'overview' })}
            onClick={() => handleNavigation(cell.row.original.platform)}
            modifier={[BtnModifier.SMALL, BtnModifier.TABLE_LINK]}
          />
        </div>
      ),
    },
  ], []);

  return (
    <>
     <MaterialReactTable
      tableInstanceRef={tableRef}
      columns={columns}
      data={props.data}
      initialState={{
        showColumnFilters: false,
        sorting: [ { id: 'name', desc: false } ],
        density: 'compact',
        columnVisibility: {
          'mrt-row-expand': false,
          detail: false,
        },
      }}
      muiTableProps={{
        sx: {
          tableLayout: 'fixed',
        },
      }}
      muiTableBodyRowProps={({ row }) => ({
        hover: true,
        onClick: (event) => {
          if (props.isLoading || props.isError) return;
          event.stopPropagation();
        },
        sx: { cursor: (props.isLoading || props.isError) ? 'not-allowed' : 'pointer' },
      })}
      muiTableBodyProps={({ row }) => ({
        sx: getStripedRowsForTable()
      })}
      muiTableHeadCellProps={({column}) => ({
        sx: {
          padding: '4px',
        },
      })}
      muiTableBodyCellProps={({cell}) => ({
        sx: {
          padding: '4px',
        },
      })}
      muiToolbarAlertBannerProps={
      props.isError
        ? {
          color: 'error',
          children: t('Error loading data'),
        }
        : undefined
    }
      enableColumnActions={false}
      enableColumnFilters={false}
      enablePagination={false}
      enableSorting={true}
      enableBottomToolbar={false}
      enableTopToolbar={false}
      defaultColumn={{
        size: 20,
        columnFilterModeOptions: [
          'fuzzy',
        ],
        enableColumnFilter: false,
        enableColumnFilterModes: false,
      }}
      state={{
        isLoading: props.isLoading,
        showAlertBanner: props.isError,
        showProgressBars: false,
        showSkeletons: props.isLoading,
        showGlobalFilter: false,
        showLoadingOverlay: props.isLoading,
      }}
      muiFilterTextFieldProps={{
        sx: { m: '0.5rem 0', width: '100%' },
      }}
      muiTableContainerProps={{
        sx: {
          minWidth: '250px',//details autoexpands not to crop rows too much, but this table does not
        },
      }}
      />
    </>
  );
}

export default LedgerLiveSyncedJobsTable;
